import { useCallback, useEffect, useRef, useState } from 'react';
import { getTaskAudio } from '../db';
import { seekSafely, useAudioUnlock } from './audioUnlock';

/** The bit of a Segment this hook needs. Deliberately not the `Segment` type. */
export interface ContextTarget {
  /** Segment id — only used to report which row is playing. */
  id: string;
  /** Task whose ORIGINAL audio blob to play from. */
  taskId: string;
  startSec: number;
  endSec: number;
}

export interface ContextPlayerState {
  /** Segment id currently playing with context, or null. */
  activeId: string | null;
  /** True while the original audio blob is being read from IndexedDB. */
  isLoading: boolean;
  /** True while context audio is actually playing. */
  isPlaying: boolean;
  error: string | null;
  /** Play `[startSec - pad, endSec + pad]` from the task's original audio. */
  play: (target: ContextTarget) => Promise<void>;
  /** Stop and clear `activeId`. Safe to call when nothing is playing. */
  stop: () => void;
  /**
   * The underlying element. Exposed so the waveform lane can read
   * `currentTime` for its playhead — this element plays the ORIGINAL file, so
   * its clock is already file time and needs no mapping.
   */
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

export interface UseContextPlayerOptions {
  /** Seconds of lead-in and tail. Default 1. */
  padSec?: number;
  /** Fired when the padded window finishes on its own (not on `stop()`). */
  onEnded?: () => void;
}

/** Give up waiting for `loadedmetadata` after this and play anyway. */
const METADATA_TIMEOUT_MS = 5000;

/** Resolve once the element can be seeked (or we've waited long enough). */
function whenSeekable(el: HTMLAudioElement): Promise<void> {
  if (el.readyState >= 1 /* HAVE_METADATA */) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let timer = 0;
    const finish = () => {
      el.removeEventListener('loadedmetadata', finish);
      el.removeEventListener('error', finish);
      window.clearTimeout(timer);
      resolve();
    };
    timer = window.setTimeout(finish, METADATA_TIMEOUT_MS);
    el.addEventListener('loadedmetadata', finish);
    el.addEventListener('error', finish);
  });
}

/**
 * "Play with context": the segment plus one second either side, sourced from
 * the task's ORIGINAL audio rather than the tight pre-cut WAV clip.
 *
 * Nothing is re-encoded. A single hidden <audio> element holds an object URL
 * for the whole original file; playing a segment is a seek to
 * `max(0, startSec - pad)` plus a watchdog that pauses at
 * `min(duration, endSec + pad)`.
 *
 * `preloadTaskId` is the task the page is currently showing. Its blob is read
 * and attached up front so the first tap doesn't have to wait on IndexedDB —
 * which matters on iOS, where playback has to follow closely enough from the
 * gesture. The URL is cached until the task changes or the page unmounts, so
 * tapping through twenty segments of one task reads the blob once.
 */
export function useContextPlayer(
  preloadTaskId: string | null,
  options: UseContextPlayerOptions = {},
): ContextPlayerState {
  const { padSec = 1, onEnded } = options;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);
  /** Which task `urlRef` belongs to. */
  const urlTaskRef = useRef<string | null>(null);
  const unlockedRef = useRef(false);
  const unlockingRef = useRef(false);
  /** Where the current window ends, in original-file seconds. */
  const stopAtRef = useRef(Number.POSITIVE_INFINITY);
  /** Bumped on every play()/stop() so stale async continuations bail out. */
  const tokenRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const playingRef = useRef(false);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // one <audio> element for the lifetime of the hook
  if (audioRef.current === null && typeof Audio !== 'undefined') {
    audioRef.current = new Audio();
    audioRef.current.preload = 'auto';
  }

  useAudioUnlock(audioRef, unlockedRef, unlockingRef);

  /** Pause + forget the active window. Does not touch the cached URL. */
  const halt = useCallback((notifyEnded: boolean) => {
    tokenRef.current += 1;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    stopAtRef.current = Number.POSITIVE_INFINITY;
    playingRef.current = false;
    const el = audioRef.current;
    if (el) el.pause();
    setIsPlaying(false);
    setIsLoading(false);
    setActiveId(null);
    if (notifyEnded) onEndedRef.current?.();
  }, []);

  const stop = useCallback(() => halt(false), [halt]);

  /* ---------------- element events ---------------- */

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    // The silent unlock drives the same element, so its events must not be
    // mistaken for a context window.
    const handlePlay = () => {
      if (!unlockingRef.current && playingRef.current) setIsPlaying(true);
    };
    const handlePause = () => setIsPlaying(false);
    // Reaching the end of the file counts as reaching the end of the window.
    const handleEnded = () => {
      if (unlockingRef.current || !playingRef.current) return;
      halt(true);
    };
    // A backstop for the rAF watchdog: rAF is throttled to ~0 fps in a
    // backgrounded tab, `timeupdate` still fires while audio plays.
    const handleTimeUpdate = () => {
      if (!playingRef.current) return;
      if (el.currentTime >= stopAtRef.current) halt(true);
    };
    const handleError = () => {
      if (unlockingRef.current) return;
      if (!el.getAttribute('src')) return; // teardown, not a failure
      setError('Could not play the original audio for this task.');
      halt(false);
    };

    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('error', handleError);

    return () => {
      el.removeEventListener('play', handlePlay);
      el.removeEventListener('pause', handlePause);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('timeupdate', handleTimeUpdate);
      el.removeEventListener('error', handleError);
    };
  }, [halt]);

  /* ---------------- object-URL cache ---------------- */

  /**
   * Detach the element from the current object URL *before* revoking it.
   * Revoking a URL that is still the element's `src` leaves a dead source
   * behind, and any play() that lands in the gap fires a spurious `error`.
   */
  const release = useCallback(() => {
    const el = audioRef.current;
    if (el && urlRef.current && el.getAttribute('src') === urlRef.current) {
      el.removeAttribute('src');
      el.load();
    }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    urlTaskRef.current = null;
  }, []);

  /** Ensure `urlRef` holds `taskId`'s audio and the element points at it. */
  const attachTask = useCallback(
    async (taskId: string): Promise<boolean> => {
      const el = audioRef.current;
      if (!el) return false;
      if (urlTaskRef.current === taskId && urlRef.current) {
        // cache hit — re-point the element if a teardown detached it
        if (el.getAttribute('src') !== urlRef.current) el.src = urlRef.current;
        return true;
      }
      const blob = await getTaskAudio(taskId);
      if (!blob) return false;
      release();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      urlTaskRef.current = taskId;
      el.src = url;
      return true;
    },
    [release],
  );

  // Warm the cache for the task the page is showing.
  useEffect(() => {
    if (!preloadTaskId) return;
    if (urlTaskRef.current === preloadTaskId) return;
    let cancelled = false;

    void (async () => {
      try {
        const blob = await getTaskAudio(preloadTaskId);
        // Don't clobber a URL that a play() picked in the meantime, and never
        // yank the source out from under audio that is currently playing.
        if (cancelled || playingRef.current || urlTaskRef.current === preloadTaskId) return;
        if (!blob) return;
        const el = audioRef.current;
        if (!el) return;
        release();
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        urlTaskRef.current = preloadTaskId;
        el.src = url;
      } catch {
        // a failed prefetch is not worth surfacing — play() reports for real
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preloadTaskId, release]);

  // stop playback and revoke on unmount
  useEffect(
    () => () => {
      tokenRef.current += 1;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      playingRef.current = false;
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        el.load();
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
      urlTaskRef.current = null;
    },
    [],
  );

  /* ---------------- play ---------------- */

  const play = useCallback(
    async (target: ContextTarget) => {
      const el = audioRef.current;
      if (!el) return;

      // A tap is a user gesture; from here on the element is blessed, and the
      // silent unlock (which may be mid-flight) must not stop what we start.
      unlockedRef.current = true;
      unlockingRef.current = false;
      el.muted = false;

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      el.pause();
      const token = ++tokenRef.current;
      const stale = () => token !== tokenRef.current;

      setError(null);
      setActiveId(target.id);
      playingRef.current = true;

      const cached = urlTaskRef.current === target.taskId && Boolean(urlRef.current);
      if (!cached) setIsLoading(true);

      let ok = false;
      try {
        ok = await attachTask(target.taskId);
      } catch {
        ok = false;
      }
      if (stale()) return;
      setIsLoading(false);

      if (!ok) {
        setError('The original audio for this task is no longer stored.');
        playingRef.current = false;
        setActiveId(null);
        return;
      }

      const from = Math.max(0, target.startSec - padSec);
      stopAtRef.current = target.endSec + padSec;

      /** Clamp the window to the real file length once we know it. */
      const clamp = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          stopAtRef.current = Math.min(el.duration, target.endSec + padSec);
        }
      };

      if (el.readyState >= 1) {
        clamp();
        seekSafely(el, from);
        try {
          await el.play();
        } catch {
          if (!stale()) halt(false);
          return;
        }
      } else {
        // Metadata isn't in yet and we must not spend the gesture waiting for
        // it: start muted at 0, then seek into place the moment we can.
        el.muted = true;
        const started = el.play().catch(() => 'rejected' as const);
        await whenSeekable(el);
        if (stale()) {
          el.muted = false;
          return;
        }
        clamp();
        seekSafely(el, from);
        el.muted = false;
        const outcome = await started;
        if (stale()) return;
        if (outcome === 'rejected' && el.paused) {
          try {
            await el.play();
          } catch {
            if (!stale()) halt(false);
            return;
          }
        }
      }

      if (stale()) return;
      setIsPlaying(true);

      /**
       * Stop at the end of the padded window. `timeupdate` alone fires only
       * every ~250 ms, which would overshoot the tail by a quarter second, so
       * rAF drives the common case and `timeupdate` covers backgrounded tabs.
       */
      const tick = () => {
        rafRef.current = null;
        if (stale() || !playingRef.current) return;
        if (el.paused) return;
        if (el.currentTime >= stopAtRef.current) {
          halt(true);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    },
    [attachTask, halt, padSec],
  );

  return { activeId, isLoading, isPlaying, error, play, stop, audioRef };
}
